import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class SpeedTestService {
    private readonly TEST_SIZE_BYTES = 25 * 1024 * 1024;          // 25 MB
    private readonly DOWNLOAD_URL = '/api/speedtest/download';
    private readonly UPLOAD_URL = '/api/speedtest/upload';
    private readonly TIMEOUT_MS = 8000;                         // abort after 8 s

    /** Runs both tests and always resolves with a result */
    async run(): Promise<{ down: number; up: number }> {
        const down = await this.safe(() => this.measureDownload());
        const up = await this.safe(() => this.measureUpload());

        return {
            down: +down.toFixed(2),
            up: +up.toFixed(2)
        };
    }

    // ───────────────── helpers ────────────────────────────────

    /** Wrap any async fn with timeout + graceful fallback (returns 0 on error) */
    private async safe(fn: () => Promise<number>): Promise<number> {
        try {
            return await fn();
        } catch (err) {
            console.warn('[SpeedTest] error:', err);
            return 0;
        }
    }

    /** Read every byte from the stream so timing is realistic */
    private async measureDownload(): Promise<number> {
        const url = `${this.DOWNLOAD_URL}?size=${this.TEST_SIZE_BYTES}&nocache=${Date.now()}`;

        const { res, t0 } = await this.fetchWithTimeout(url, { cache: 'no-store' });
        if (!res.body) throw new Error('ReadableStream unsupported');

        const reader = res.body.getReader();
        let bytes = 0;

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            bytes += value.length;
        }

        const ms = performance.now() - t0;
        return (bytes * 8) / ms / 1000;              // Mb/s
    }

    /** POST an equally large blob – server discards it */
    private async measureUpload(): Promise<number> {
        const blob = new Blob([new Uint8Array(this.TEST_SIZE_BYTES)]);
        const { t0 } = await this.fetchWithTimeout(
            this.UPLOAD_URL,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/octet-stream' },
                body: blob,
                cache: 'no-store'
            }
        );

        const ms = performance.now() - t0;
        return (this.TEST_SIZE_BYTES * 8) / ms / 1000;  // Mb/s
    }

    /** fetch() with AbortController-based timeout */
    private async fetchWithTimeout(
        input: RequestInfo | URL,
        init: RequestInit = {}
    ): Promise<{ res: Response; t0: number }> {
        const ctrl = new AbortController();
        const id = setTimeout(() => ctrl.abort(), this.TIMEOUT_MS);

        try {
            const t0 = performance.now();
            const res = await fetch(input, { ...init, signal: ctrl.signal });
            clearTimeout(id);

            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return { res, t0 };
        } catch (err) {
            clearTimeout(id);
            throw err;
        }
    }
}
